import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export default function VocabularyQuiz({
  terms = [],
  difficultyStats = [],
  getTranslationsForTerm,
  getVocabularyProgressForTerm,
  vocabularyLanguage,
  submitVocabularyQuizResult,
  goBack,
  styles,
}) {
  const QUESTION_COUNT = 10;

  // =========================================================
  // QUIZ SETTINGS
  // =========================================================

  const [difficultyLevel, setDifficultyLevel] =
    useState("normal");

  // =========================================================
  // GLOBAL DIFFICULTY MAP
  // =========================================================

  const difficultyMap = useMemo(() => {
    return new Map(
      (difficultyStats || []).map((row) => [
        row.term_id,
        row,
      ])
    );
  }, [difficultyStats]);

  // =========================================================
  // KEEP LATEST ADAPTIVE DATA IN REFS
  //
  // This is important:
  // progress can change after every answer, but that should
  // NOT automatically regenerate the current quiz.
  // =========================================================

  const progressGetterRef = useRef(
    getVocabularyProgressForTerm
  );

  const translationGetterRef = useRef(
    getTranslationsForTerm
  );

  const difficultyMapRef = useRef(
    difficultyMap
  );

  useEffect(() => {
    progressGetterRef.current =
      getVocabularyProgressForTerm;
  }, [getVocabularyProgressForTerm]);

  useEffect(() => {
    translationGetterRef.current =
      getTranslationsForTerm;
  }, [getTranslationsForTerm]);

  useEffect(() => {
    difficultyMapRef.current =
      difficultyMap;
  }, [difficultyMap]);

  // =========================================================
  // BUILD QUIZ QUESTIONS
  //
  // IMPORTANT:
  // This function builds a snapshot.
  // Once saved into questions state, that round is frozen.
  // =========================================================

  const buildQuizQuestions = useCallback(
    (requestedDifficulty) => {
      if (terms.length < 4) {
        return [];
      }

      const shuffle = (items) => {
        return [...items].sort(
          () => Math.random() - 0.5
        );
      };

      const pickRandom = (
        items,
        count
      ) => {
        return shuffle(items).slice(
          0,
          Math.min(
            count,
            items.length
          )
        );
      };

      // =====================================================
      // ENRICH TERMS WITH PERSONAL + GLOBAL DATA
      // =====================================================

      const enrichedTerms =
        terms.map((term) => {
          const progress =
            progressGetterRef.current?.(
              term.id
            );

          const globalStats =
            difficultyMapRef.current.get(
              term.id
            );

          const masteryLevel =
            progress?.mastery_level ?? 0;

          const reviewCount =
            progress?.review_count || 0;

          const correctCount =
            progress?.correct_count || 0;

          const personalAccuracy =
            reviewCount > 0
              ? (correctCount /
                  reviewCount) *
                100
              : null;

          const globalAttempts =
            Number(
              globalStats?.total_attempts ||
                0
            );

          const globalDifficulty =
            Number(
              globalStats?.difficulty_pct ||
                0
            );

          // ===============================================
          // CATEGORY
          // ===============================================

          let category = "new";

          if (reviewCount === 0) {
            category = "new";
          } else if (
            masteryLevel >= 3 &&
            personalAccuracy >= 80
          ) {
            category = "strong";
          } else if (
            reviewCount >= 2 &&
            (
              personalAccuracy < 60 ||
              masteryLevel === 0
            )
          ) {
            category = "weak";
          } else {
            category = "learning";
          }

          // ===============================================
          // ADAPTIVE SCORE
          // ===============================================

          const masteryWeakness =
            ((3 - masteryLevel) / 3) *
            100;

          const personalErrorRate =
            personalAccuracy === null
              ? 0
              : 100 -
                personalAccuracy;

          const globalWeight =
            Math.min(
              globalAttempts / 20,
              1
            );

          const adjustedGlobalDifficulty =
            globalDifficulty *
            globalWeight;

          const adaptiveScore =
            category === "new"
              ? 50
              : masteryWeakness *
                  0.5 +
                personalErrorRate *
                  0.3 +
                adjustedGlobalDifficulty *
                  0.2;

          return {
            term,
            category,
            adaptiveScore,
            masteryLevel,
            reviewCount,
            personalAccuracy,
            globalDifficulty,
            globalAttempts,
          };
        });

      // =====================================================
      // SPLIT INTO ADAPTIVE GROUPS
      // =====================================================

      const newWords =
        enrichedTerms.filter(
          (item) =>
            item.category === "new"
        );

      const strongWords =
        enrichedTerms.filter(
          (item) =>
            item.category ===
            "strong"
        );

      const learningWords =
        enrichedTerms.filter(
          (item) =>
            item.category ===
            "learning"
        );

      const weakWords =
        enrichedTerms.filter(
          (item) =>
            item.category === "weak"
        );

      let selected = [];

      // =====================================================
      // EASY
      //
      // 7 strong / learning
      // 3 new
      // =====================================================

      if (
        requestedDifficulty ===
        "easy"
      ) {
        const easierKnown = [
          ...strongWords,
          ...learningWords,
        ].sort(
          (a, b) =>
            a.adaptiveScore -
            b.adaptiveScore
        );

        selected = [
          ...pickRandom(
            easierKnown,
            7
          ),

          ...pickRandom(
            newWords,
            3
          ),
        ];
      }

      // =====================================================
      // NORMAL
      //
      // 3 strong
      // 4 new / learning
      // 3 weak
      // =====================================================

      if (
        requestedDifficulty ===
        "normal"
      ) {
        selected = [
          ...pickRandom(
            strongWords,
            3
          ),

          ...pickRandom(
            [
              ...newWords,
              ...learningWords,
            ],
            4
          ),

          ...pickRandom(
            weakWords,
            3
          ),
        ];
      }

      // =====================================================
      // HARD
      //
      // 7 weak
      // 3 new / learning
      // =====================================================

      if (
        requestedDifficulty ===
        "hard"
      ) {
        const hardestWeak = [
          ...weakWords,
        ].sort(
          (a, b) =>
            b.adaptiveScore -
            a.adaptiveScore
        );

        selected = [
          ...pickRandom(
            hardestWeak,
            7
          ),

          ...pickRandom(
            [
              ...newWords,
              ...learningWords,
            ],
            3
          ),
        ];
      }

      // =====================================================
      // REMOVE DUPLICATES
      // =====================================================

      const selectedMap =
        new Map();

      selected.forEach((item) => {
        selectedMap.set(
          item.term.id,
          item
        );
      });

      selected = [
        ...selectedMap.values(),
      ];

      // =====================================================
      // FALLBACK
      //
      // If requested groups don't have enough words,
      // fill the remaining slots from the topic.
      // =====================================================

      if (
        selected.length <
        QUESTION_COUNT
      ) {
        const selectedIds =
          new Set(
            selected.map(
              (item) =>
                item.term.id
            )
          );

        const remaining =
          enrichedTerms.filter(
            (item) =>
              !selectedIds.has(
                item.term.id
              )
          );

        selected = [
          ...selected,

          ...pickRandom(
            remaining,
            QUESTION_COUNT -
              selected.length
          ),
        ];
      }

      // =====================================================
      // FINAL SELECTED TERMS
      // =====================================================

      const finalSelected =
        shuffle(selected).slice(
          0,
          Math.min(
            QUESTION_COUNT,
            selected.length
          )
        );

      // =====================================================
      // BUILD QUESTIONS + DISTRACTORS
      // =====================================================

      return finalSelected.map(
        ({ term }) => {
          const translations =
            translationGetterRef.current?.(
              term.id
            ) || {};

          const correctTranslation =
            translations?.[
              vocabularyLanguage
            ] || "";

          // Distractors stay inside
          // the SAME TOPIC.
          const distractorTerms =
            shuffle(
              terms.filter(
                (item) =>
                  item.id !==
                  term.id
              )
            ).slice(0, 3);

          const distractors =
            distractorTerms
              .map((item) => {
                const itemTranslations =
                  translationGetterRef.current?.(
                    item.id
                  ) || {};

                return {
                  termId:
                    item.id,

                  text:
                    itemTranslations?.[
                      vocabularyLanguage
                    ] || "",
                };
              })
              .filter(
                (item) =>
                  item.text
              );

          const options =
            shuffle([
              {
                termId:
                  term.id,

                text:
                  correctTranslation,

                isCorrect:
                  true,
              },

              ...distractors.map(
                (item) => ({
                  ...item,
                  isCorrect:
                    false,
                })
              ),
            ]);

          return {
            term,
            correctTranslation,
            options,
          };
        }
      );
    },
    [
      terms,
      vocabularyLanguage,
    ]
  );

  // =========================================================
  // FROZEN QUIZ QUESTIONS
  //
  // This replaces the old useMemo questions architecture.
  // =========================================================

  const [questions, setQuestions] =
    useState([]);

  // =========================================================
  // QUIZ STATE
  // =========================================================

  const [
    currentIndex,
    setCurrentIndex,
  ] = useState(0);

  const [
    selectedOption,
    setSelectedOption,
  ] = useState(null);

  const [
    hasAnswered,
    setHasAnswered,
  ] = useState(false);

  const [
    isCorrect,
    setIsCorrect,
  ] = useState(false);

  const [
    correctCount,
    setCorrectCount,
  ] = useState(0);

  const [
    incorrectCount,
    setIncorrectCount,
  ] = useState(0);

  const [
    quizComplete,
    setQuizComplete,
  ] = useState(false);

  const [
    activitySessionId,
    setActivitySessionId,
  ] = useState(() =>
    crypto.randomUUID()
  );

  const [
    savingAnswer,
    setSavingAnswer,
  ] = useState(false);

  const [
    saveError,
    setSaveError,
  ] = useState("");

  const currentQuestion =
    questions[currentIndex];

  // =========================================================
  // RESET ROUND STATE
  // =========================================================

  const resetRoundState =
    useCallback(() => {
      setCurrentIndex(0);

      setSelectedOption(null);

      setHasAnswered(false);

      setIsCorrect(false);

      setCorrectCount(0);

      setIncorrectCount(0);

      setQuizComplete(false);

      setSavingAnswer(false);

      setSaveError("");

      setActivitySessionId(
        crypto.randomUUID()
      );
    }, []);

  // =========================================================
  // START A NEW FROZEN ROUND
  // =========================================================

  const startNewQuizRound =
    useCallback(
      (newDifficulty) => {
        const newQuestions =
          buildQuizQuestions(
            newDifficulty
          );

        setQuestions(
          newQuestions
        );

        resetRoundState();
      },
      [
        buildQuizQuestions,
        resetRoundState,
      ]
    );

  // =========================================================
  // INITIAL QUIZ
  //
  // Generate only when the underlying topic/language
  // question builder changes.
  //
  // Progress updates DO NOT change buildQuizQuestions,
  // because progress is read through a ref.
  // =========================================================

  useEffect(() => {
    if (terms.length >= 4) {
      startNewQuizRound(
        difficultyLevel
      );
    } else {
      setQuestions([]);
    }

    // Intentionally do NOT include difficultyLevel here.
    // Difficulty changes are handled explicitly by buttons.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    terms,
    vocabularyLanguage,
    startNewQuizRound,
  ]);

  // =========================================================
  // ANSWER QUESTION
  // =========================================================

  const handleAnswer = async (
    option
  ) => {
    if (
      hasAnswered ||
      savingAnswer ||
      !currentQuestion
    ) {
      return;
    }

    // Freeze the exact question being answered
    // before any async database update occurs.
    const answeredQuestion =
      currentQuestion;

    const answerIsCorrect =
      option.isCorrect;

    setSelectedOption(option);

    setHasAnswered(true);

    setIsCorrect(
      answerIsCorrect
    );

    setSavingAnswer(true);

    setSaveError("");

    const result =
      await submitVocabularyQuizResult(
        {
          termId:
            answeredQuestion.term.id,

          sessionId:
            activitySessionId,

          isCorrect:
            answerIsCorrect,

          selectedTermId:
            option.termId,
        }
      );

    setSavingAnswer(false);

    if (!result?.success) {
      setSaveError(
        result?.error ||
          "Failed to save quiz result."
      );

      return;
    }

    if (answerIsCorrect) {
      setCorrectCount(
        (prev) => prev + 1
      );
    } else {
      setIncorrectCount(
        (prev) => prev + 1
      );
    }
  };

  // =========================================================
  // NEXT QUESTION
  // =========================================================

  const goNext = () => {
    if (savingAnswer) {
      return;
    }

    if (
      currentIndex >=
      questions.length - 1
    ) {
      setQuizComplete(true);
      return;
    }

    setCurrentIndex(
      (prev) => prev + 1
    );

    setSelectedOption(null);

    setHasAnswered(false);

    setIsCorrect(false);

    setSaveError("");
  };

  // =========================================================
  // RESTART SAME DIFFICULTY
  // =========================================================

  const restartQuiz = () => {
    startNewQuizRound(
      difficultyLevel
    );
  };

  // =========================================================
  // CHANGE DIFFICULTY
  // =========================================================

  const changeDifficulty = (
    newDifficulty
  ) => {
    if (
      savingAnswer ||
      newDifficulty ===
        difficultyLevel
    ) {
      return;
    }

    setDifficultyLevel(
      newDifficulty
    );

    startNewQuizRound(
      newDifficulty
    );
  };

  // =========================================================
  // AUTO RECOMMENDATION
  // =========================================================

  const getRecommendedQuizDifficulty =
    () => {
      const totalQuestions =
        questions.length;

      if (
        totalQuestions === 0
      ) {
        return difficultyLevel;
      }

      const accuracy =
        Math.round(
          (correctCount /
            totalQuestions) *
            100
        );

      // EASY
      if (
        difficultyLevel === "easy"
      ) {
        return accuracy >= 90
          ? "normal"
          : "easy";
      }

      // NORMAL
      if (
        difficultyLevel ===
        "normal"
      ) {
        if (accuracy >= 85) {
          return "hard";
        }

        if (accuracy < 65) {
          return "easy";
        }

        return "normal";
      }

      // HARD
      if (
        difficultyLevel === "hard"
      ) {
        if (accuracy < 60) {
          return "normal";
        }

        return "hard";
      }

      return difficultyLevel;
    };

  const getQuizDifficultyLabel = (
    level
  ) => {
    if (level === "easy") {
      return "Easy";
    }

    if (level === "hard") {
      return "Hard";
    }

    return "Normal";
  };

  // =========================================================
  // CONTINUE WITH RECOMMENDED LEVEL
  // =========================================================

  const continueWithRecommended =
    (recommendedDifficulty) => {
      setDifficultyLevel(
        recommendedDifficulty
      );

      startNewQuizRound(
        recommendedDifficulty
      );
    };

  // =========================================================
  // EMPTY STATE
  // =========================================================

  if (terms.length < 4) {
    return (
      <div style={styles.card}>
        <button
          style={
            styles.secondaryButton
          }
          onClick={goBack}
        >
          Back
        </button>

        <div
          style={styles.emptyBox}
        >
          At least 4 vocabulary
          terms are required to
          start a quiz.
        </div>
      </div>
    );
  }

  // =========================================================
  // LOADING / INITIALIZING
  // =========================================================

  if (
    questions.length === 0
  ) {
    return (
      <div style={styles.card}>
        <div
          style={styles.emptyBox}
        >
          Preparing quiz...
        </div>
      </div>
    );
  }

  // =========================================================
  // QUIZ COMPLETE
  // =========================================================

  if (quizComplete) {
    const accuracy =
      questions.length > 0
        ? Math.round(
            (correctCount /
              questions.length) *
              100
          )
        : 0;

    const recommendedDifficulty =
      getRecommendedQuizDifficulty();

    return (
      <div style={styles.card}>
        <div
          style={{
            textAlign: "center",
            padding: "40px 20px",
          }}
        >
          <div
            style={{
              fontSize: "42px",
              marginBottom: "12px",
            }}
          >
            🎉
          </div>

          <h2
            style={
              styles.sectionTitle
            }
          >
            Quiz Complete
          </h2>

          <div
            style={{
              marginTop: "8px",
              color: "#6b7280",
            }}
          >
            You completed a{" "}
            <strong>
              {getQuizDifficultyLabel(
                difficultyLevel
              )}
            </strong>{" "}
            quiz.
          </div>

          {/* =================================================
              RESULTS
          ================================================= */}

          <div
            style={{
              marginTop: "24px",

              display: "grid",

              gridTemplateColumns:
                "repeat(auto-fit, minmax(130px, 1fr))",

              gap: "12px",
            }}
          >
            <div
              style={
                styles.emptyBox
              }
            >
              <strong>
                Questions
              </strong>

              <div
                style={{
                  marginTop: "6px",
                  fontSize: "22px",
                  fontWeight: "700",
                }}
              >
                {
                  questions.length
                }
              </div>
            </div>

            <div
              style={
                styles.emptyBox
              }
            >
              <strong>
                Correct
              </strong>

              <div
                style={{
                  marginTop: "6px",
                  fontSize: "22px",
                  fontWeight: "700",
                }}
              >
                {correctCount}
              </div>
            </div>

            <div
              style={
                styles.emptyBox
              }
            >
              <strong>
                Incorrect
              </strong>

              <div
                style={{
                  marginTop: "6px",
                  fontSize: "22px",
                  fontWeight: "700",
                }}
              >
                {incorrectCount}
              </div>
            </div>

            <div
              style={
                styles.emptyBox
              }
            >
              <strong>
                Accuracy
              </strong>

              <div
                style={{
                  marginTop: "6px",
                  fontSize: "22px",
                  fontWeight: "700",
                }}
              >
                {accuracy}%
              </div>
            </div>
          </div>

          {/* =================================================
              AUTO RECOMMENDATION
          ================================================= */}

          <div
            style={{
              marginTop: "24px",
              padding: "18px",

              border:
                "1px solid #dbeafe",

              borderRadius: "14px",

              backgroundColor:
                "#eff6ff",
            }}
          >
            <div
              style={{
                color: "#6b7280",
                fontSize: "14px",
                fontWeight: "700",
              }}
            >
              Recommended Next
              Level
            </div>

            <div
              style={{
                marginTop: "8px",
                fontSize: "24px",
                fontWeight: "700",
              }}
            >
              {getQuizDifficultyLabel(
                recommendedDifficulty
              )}
            </div>

            <div
              style={{
                marginTop: "8px",
                color: "#6b7280",
              }}
            >
              Your quiz accuracy
              was{" "}
              <strong>
                {accuracy}%
              </strong>
              .
            </div>

            <div
              style={{
                marginTop: "6px",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              {recommendedDifficulty ===
              difficultyLevel
                ? `Keep practicing at ${getQuizDifficultyLabel(
                    recommendedDifficulty
                  )} level.`
                : `Based on this quiz, we recommend moving to ${getQuizDifficultyLabel(
                    recommendedDifficulty
                  )}.`}
            </div>
          </div>

          {/* =================================================
              COMPLETE ACTIONS
          ================================================= */}

          <div
            style={{
              marginTop: "28px",

              display: "flex",

              justifyContent:
                "center",

              gap: "12px",

              flexWrap: "wrap",
            }}
          >
            <button
              style={
                styles.smallPrimaryButton
              }
              onClick={() =>
                continueWithRecommended(
                  recommendedDifficulty
                )
              }
            >
              Continue with{" "}
              {getQuizDifficultyLabel(
                recommendedDifficulty
              )}
            </button>

            <button
              style={
                styles.secondaryButton
              }
              onClick={
                restartQuiz
              }
            >
              Play{" "}
              {getQuizDifficultyLabel(
                difficultyLevel
              )}{" "}
              Again
            </button>

            <button
              style={
                styles.secondaryButton
              }
              onClick={goBack}
            >
              Finish
            </button>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================
  // MAIN QUIZ
  // =========================================================

  return (
    <div style={styles.card}>
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div
        style={styles.topBar}
      >
        <div>
          <h2
            style={
              styles.sectionTitle
            }
          >
            Vocabulary Quiz
          </h2>

          <div
            style={
              styles.quizPath
            }
          >
            Question{" "}
            {currentIndex + 1} of{" "}
            {questions.length}
          </div>
        </div>

        <button
          style={
            styles.secondaryButton
          }
          onClick={goBack}
        >
          Back
        </button>
      </div>

      {/* =====================================================
          DIFFICULTY SELECTOR
      ===================================================== */}

      <div
        style={{
          display: "flex",

          alignItems: "center",

          gap: "10px",

          marginTop: "18px",

          flexWrap: "wrap",
        }}
      >
        <strong>
          Difficulty:
        </strong>

        {[
          "easy",
          "normal",
          "hard",
        ].map((level) => (
          <button
            key={level}
            disabled={
              savingAnswer
            }
            onClick={() =>
              changeDifficulty(
                level
              )
            }
            style={{
              padding:
                "8px 14px",

              borderRadius:
                "8px",

              border:
                difficultyLevel ===
                level
                  ? "2px solid #2563eb"
                  : "1px solid #d1d5db",

              backgroundColor:
                difficultyLevel ===
                level
                  ? "#eff6ff"
                  : "#ffffff",

              fontWeight:
                difficultyLevel ===
                level
                  ? "700"
                  : "500",

              cursor:
                savingAnswer
                  ? "default"
                  : "pointer",
            }}
          >
            {getQuizDifficultyLabel(
              level
            )}
          </button>
        ))}

        <span
          style={{
            color: "#6b7280",
            fontSize: "13px",
          }}
        >
          Current:{" "}
          <strong>
            {getQuizDifficultyLabel(
              difficultyLevel
            )}
          </strong>
        </span>
      </div>

      {/* =====================================================
          QUESTION
      ===================================================== */}

      <div
        style={{
          marginTop: "28px",
          padding: "24px",

          border:
            "1px solid #e5e7eb",

          borderRadius: "14px",
        }}
      >
        <div
          style={{
            color: "#6b7280",
            fontSize: "14px",
          }}
        >
          What does this term
          mean?
        </div>

        <div
          style={{
            marginTop: "10px",

            fontSize: "28px",

            fontWeight: "700",
          }}
        >
          {
            currentQuestion
              .term
              .english_term
          }
        </div>
      </div>

      {/* =====================================================
          OPTIONS
      ===================================================== */}

      <div
        style={{
          marginTop: "20px",
          display: "grid",
          gap: "12px",
        }}
      >
        {currentQuestion.options.map(
          (option, index) => {
            const selected =
              selectedOption?.termId ===
              option.termId;

            let backgroundColor =
              "#ffffff";

            let border =
              "1px solid #d1d5db";

            if (hasAnswered) {
              if (
                option.isCorrect
              ) {
                backgroundColor =
                  "#dcfce7";

                border =
                  "1px solid #86efac";
              } else if (
                selected
              ) {
                backgroundColor =
                  "#fee2e2";

                border =
                  "1px solid #fca5a5";
              }
            }

            return (
              <button
                key={
                  option.termId
                }
                onClick={() =>
                  handleAnswer(
                    option
                  )
                }
                disabled={
                  hasAnswered ||
                  savingAnswer
                }
                style={{
                  padding: "16px",

                  borderRadius:
                    "12px",

                  border,

                  backgroundColor,

                  textAlign:
                    "left",

                  fontSize: "16px",

                  cursor:
                    hasAnswered ||
                    savingAnswer
                      ? "default"
                      : "pointer",
                }}
              >
                <strong>
                  {String.fromCharCode(
                    65 + index
                  )}
                  .
                </strong>{" "}
                {option.text}
              </button>
            );
          }
        )}
      </div>

      {/* =====================================================
          SAVE STATUS
      ===================================================== */}

      {savingAnswer && (
        <div
          style={{
            marginTop: "12px",
            color: "#6b7280",
          }}
        >
          Saving progress...
        </div>
      )}

      {saveError && (
        <div
          style={{
            marginTop: "12px",

            padding:
              "10px 14px",

            borderRadius:
              "10px",

            backgroundColor:
              "#fee2e2",

            color: "#991b1b",

            fontWeight: "600",
          }}
        >
          Unable to save
          progress:{" "}
          {saveError}
        </div>
      )}

      {/* =====================================================
          FEEDBACK
      ===================================================== */}

      {hasAnswered && (
        <div
          style={{
            marginTop: "20px",

            padding: "14px",

            borderRadius:
              "10px",

            backgroundColor:
              isCorrect
                ? "#dcfce7"
                : "#fee2e2",

            color: isCorrect
              ? "#166534"
              : "#991b1b",
          }}
        >
          <div
            style={{
              fontWeight: "700",
            }}
          >
            {isCorrect
              ? "Correct!"
              : "Incorrect"}
          </div>

          {!isCorrect && (
            <div
              style={{
                marginTop: "6px",
              }}
            >
              Correct answer:{" "}
              <strong>
                {
                  currentQuestion
                    .correctTranslation
                }
              </strong>
            </div>
          )}
        </div>
      )}

      {/* =====================================================
          NEXT
      ===================================================== */}

      {hasAnswered && (
        <div
          style={{
            marginTop: "22px",

            display: "flex",

            justifyContent:
              "flex-end",
          }}
        >
          <button
            style={
              styles.smallPrimaryButton
            }
            onClick={goNext}
            disabled={
              savingAnswer
            }
          >
            {currentIndex ===
            questions.length - 1
              ? "Finish Quiz"
              : "Next Question"}
          </button>
        </div>
      )}
    </div>
  );
}